import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';

import useRoles from 'common/hooks/useRoles';

import { LABEL_VALUE_ROW_GAP, PatientLabelValueSkeleton } from '../PatientLabelValue';

import PartnerContactListItem from './PartnerContactListItem';
import PartnerContactsDialog from './PartnerContactsDialog';
import partnerContactPropType from './partnerContactPropType';

// How many partners fit the fixed-height header card; a longer list scrolls.
const PARTNERS_SHOWN_WITHOUT_SCROLLING = 3;

// The email and phone listed under each partner are the patient's, as that partner has them.
const PatientPartnersColumn = ({ mpi, patientName, partnerContacts, isPatientLoaded }) => {
  const hasAccess = useRoles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canEditPartnerContacts = hasAccess.editPartnerContact;

  const renderPartnerList = () => {
    if (!partnerContacts.length) {
      return (
        <Typography variant='body2' color='text.secondary'>
          No partners
        </Typography>
      );
    }

    return (
      <Box
        role='list'
        aria-label='Patient contact by partner'
        // Focusable so a keyboard user can scroll a list taller than the card.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          // The fade takes 28px of its own, so only a list longer than the card gets it (three
          // partners are an exact fit). It stays pinned to the bottom edge while more partners are
          // below; once scrolled to the end it sits under the last partner, over empty space.
          ...(partnerContacts.length > PARTNERS_SHOWN_WITHOUT_SCROLLING && {
            '&::after': {
              content: '""',
              display: 'block',
              position: 'sticky',
              bottom: 0,
              height: 28,
              pointerEvents: 'none',
              background: (theme) =>
                `linear-gradient(${alpha(theme.palette.background.paper, 0)}, ${
                  theme.palette.background.paper
                })`,
            },
          }),
        }}
      >
        {map(partnerContacts, (partnerContact) => (
          <PartnerContactListItem key={partnerContact.partnerId} partnerContact={partnerContact} />
        ))}
      </Box>
    );
  };

  return (
    <Box
      component='section'
      aria-label='Patient contact by partner'
      sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}
    >
      {isPatientLoaded ? (
        <>
          {/* The pencil sits right after the heading, so it reads as editing these contacts. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
            <Typography component='h3' variant='subtitle2'>
              {'Patient contact by partner '}
              <Box component='span' sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {`(${partnerContacts.length})`}
              </Box>
            </Typography>

            {canEditPartnerContacts && (
              <Tooltip title='Edit partner contacts'>
                <IconButton
                  size='small'
                  aria-label='Edit partner contacts'
                  onClick={() => setIsDialogOpen(true)}
                  sx={{
                    width: 26,
                    height: 26,
                    color: 'action.active',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5 }}>
            How each partner reaches this patient
          </Typography>

          {renderPartnerList()}
        </>
      ) : (
        // While loading: a line for the heading and three below it, in step with the other columns.
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: LABEL_VALUE_ROW_GAP }}>
          {map([1, 2, 3, 4], (skeletonLine) => (
            <PatientLabelValueSkeleton key={skeletonLine} />
          ))}
        </Box>
      )}

      {isDialogOpen && (
        <PartnerContactsDialog
          mpi={mpi}
          patientName={patientName}
          partnerContacts={partnerContacts}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </Box>
  );
};

PatientPartnersColumn.propTypes = {
  mpi: PropTypes.string.isRequired,
  patientName: PropTypes.string.isRequired,
  partnerContacts: PropTypes.arrayOf(partnerContactPropType),
  isPatientLoaded: PropTypes.bool.isRequired,
};

PatientPartnersColumn.defaultProps = {
  partnerContacts: [],
};

export default PatientPartnersColumn;
